// Localização: frontend/src/pages/Escalas/DetalhesEscalaPage.jsx
// Página de detalhes da escala com calendário visual

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
    ArrowLeft,
    Calendar,
    Clock,
    Users,
    User,
    Download,
    FileText,
    Filter,
    ChevronLeft,
    ChevronRight,
    X,
    Trash2,
    AlertCircle
} from 'lucide-react';
import Modal from '../../components/UI/Modal';
import PrimaryButton from '../../components/Common/PrimaryButton';
import { obterEscala, excluirEscala } from '../../services/escalaService';
import { exportarEscala } from '../../utils/escalaExportUtils';
import { useAuth } from '../../context/AuthContext';
import LoadingIndicator from '../../components/Common/LoadingIndicator';
import Alert from '../../components/UI/Alert';
import SecondaryButton from '../../components/Common/SecondaryButton';
import EstatisticasDetalhadas from '../../components/Escalas/EstatisticasDetalhadas';

moment.locale('pt-br');

const DetalhesEscalaPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [escala, setEscala] = useState(null);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('calendar'); // 'calendar' ou 'list'
    const [filterProfissional, setFilterProfissional] = useState(null);
    const [filterPaciente, setFilterPaciente] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(moment());
    const [diaSelecionado, setDiaSelecionado] = useState(null);
    const [showDetalhesDia, setShowDetalhesDia] = useState(false);
    const [showConfirmarExclusao, setShowConfirmarExclusao] = useState(false);
    const [excluindo, setExcluindo] = useState(false);

    useEffect(() => {
        carregarEscala();
    }, [id]);

    const carregarEscala = async () => {
        setLoading(true);
        setError(null);
        try {
            const dados = await obterEscala(id);
            
            // Transformar dados para o formato esperado
            const dadosFormatados = {
                id: dados.id,
                periodo_inicio: dados.periodo_inicio,
                periodo_fim: dados.periodo_fim,
                status: dados.status,
                origem: dados.origem,
                itens: (dados.itens || []).map(item => ({
                    usuario_id: item.usuario_id,
                    usuario_nome: item.usuario_nome,
                    data_plantao: item.data_plantao,
                    hora_inicio: item.hora_inicio,
                    hora_fim: item.hora_fim,
                    tipo_plantao: item.tipo_plantao,
                    paciente_id: item.paciente_id,
                    paciente_nome: item.paciente_nome,
                    justificativa: item.observacoes
                })),
                estatisticas: {
                    distribuicao_equitativa_score: 0,
                    preferencias_atendidas_score: 0,
                    conflitos_detectados: 0
                }
            };
            
            setEscala(dadosFormatados);
            
            // Definir mês inicial como o início do período
            if (dados.periodo_inicio) {
                setCurrentMonth(moment(dados.periodo_inicio));
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Erro ao carregar escala';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const getTipoPlantaoColor = (tipo) => {
        const colors = {
            'diurno': 'bg-blue-100 text-blue-800 border-blue-300',
            'noturno': 'bg-purple-100 text-purple-800 border-purple-300',
            'integral': 'bg-green-100 text-green-800 border-green-300',
            'madrugada': 'bg-indigo-100 text-indigo-800 border-indigo-300'
        };
        return colors[tipo] || 'bg-gray-100 text-gray-800 border-gray-300';
    };

    const formatarHora = (hora) => {
        if (!hora) return '';
        return hora.substring(0, 5); // HH:MM
    };

    // Filtrar itens
    const itensFiltrados = escala?.itens.filter(item => {
        if (filterProfissional && item.usuario_nome !== filterProfissional) return false;
        if (filterPaciente && item.paciente_nome !== filterPaciente) return false;
        return true;
    }) || [];

    // Agrupar itens por data para o calendário
    const itensPorData = itensFiltrados.reduce((acc, item) => {
        // Normalizar formato da data (pode vir como YYYY-MM-DD ou outro formato)
        let data = item.data_plantao;
        if (data) {
            // Garantir formato YYYY-MM-DD
            const dataMoment = moment(data);
            if (dataMoment.isValid()) {
                data = dataMoment.format('YYYY-MM-DD');
            }
        }
        if (!acc[data]) {
            acc[data] = [];
        }
        acc[data].push(item);
        return acc;
    }, {});

    // Gerar dias do mês atual
    const gerarDiasDoMes = () => {
        const inicioMes = currentMonth.clone().startOf('month');
        const fimMes = currentMonth.clone().endOf('month');
        const inicioPeriodo = escala ? moment(escala.periodo_inicio) : inicioMes;
        const fimPeriodo = escala ? moment(escala.periodo_fim) : fimMes;
        
        const dias = [];
        let dataAtual = inicioMes.clone();
        
        // Adicionar dias do mês anterior para completar a primeira semana
        const primeiroDiaSemana = inicioMes.day();
        for (let i = primeiroDiaSemana - 1; i >= 0; i--) {
            const dia = inicioMes.clone().subtract(i + 1, 'days');
            dias.push({
                data: dia,
                isCurrentMonth: false,
                isInPeriod: dia.isSameOrAfter(inicioPeriodo, 'day') && dia.isSameOrBefore(fimPeriodo, 'day')
            });
        }
        
        // Adicionar dias do mês atual
        while (dataAtual.isSameOrBefore(fimMes, 'day')) {
            dias.push({
                data: dataAtual.clone(),
                isCurrentMonth: true,
                isInPeriod: dataAtual.isSameOrAfter(inicioPeriodo, 'day') && dataAtual.isSameOrBefore(fimPeriodo, 'day')
            });
            dataAtual.add(1, 'day');
        }
        
        // Completar última semana
        const ultimoDiaSemana = fimMes.day();
        for (let i = 1; i <= (6 - ultimoDiaSemana); i++) {
            const dia = fimMes.clone().add(i, 'days');
            dias.push({
                data: dia,
                isCurrentMonth: false,
                isInPeriod: dia.isSameOrAfter(inicioPeriodo, 'day') && dia.isSameOrBefore(fimPeriodo, 'day')
            });
        }
        
        return dias;
    };

    const diasDoMes = gerarDiasDoMes();
    const diasSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

    // Obter profissionais e pacientes únicos para filtros
    const profissionais = [...new Set(escala?.itens.map(i => i.usuario_nome) || [])].sort();
    const pacientes = [...new Set(escala?.itens.map(i => i.paciente_nome).filter(Boolean) || [])].sort();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <LoadingIndicator message="Carregando detalhes da escala..." />
            </div>
        );
    }

    if (error || !escala) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4">
                <div className="text-center">
                    <Alert type="error" message={error || 'Escala não encontrada'} />
                    <SecondaryButton onClick={() => navigate('/escalas')} className="mt-4">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Voltar para Escalas
                    </SecondaryButton>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <SecondaryButton
                                onClick={() => navigate('/escalas')}
                                className="flex items-center gap-2"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Voltar
                            </SecondaryButton>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    Escala de {moment(escala.periodo_inicio).format('MMMM [de] YYYY')}
                                </h1>
                                <p className="text-sm text-gray-600 mt-1">
                                    {moment(escala.periodo_inicio).format('DD/MM/YYYY')} - {moment(escala.periodo_fim).format('DD/MM/YYYY')}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <SecondaryButton
                                onClick={() => setShowConfirmarExclusao(true)}
                                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white"
                                disabled={excluindo}
                            >
                                <Trash2 className="w-4 h-4" />
                                Excluir Escala
                            </SecondaryButton>
                            <div className="relative group">
                                <SecondaryButton
                                    onClick={() => {}}
                                    className="flex items-center gap-2"
                                >
                                    <Download className="w-4 h-4" />
                                    Exportar
                                </SecondaryButton>
                                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                                    <button
                                        onClick={async () => {
                                            try {
                                                await exportarEscala(escala, 'pdf', user?.organizacao_nome || '');
                                            } catch (error) {
                                                console.error('Erro ao exportar PDF:', error);
                                            }
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 rounded-t-lg"
                                    >
                                        <FileText className="w-4 h-4" />
                                        Exportar PDF
                                    </button>
                                    <button
                                        onClick={async () => {
                                            try {
                                                await exportarEscala(escala, 'csv', user?.organizacao_nome || '');
                                            } catch (error) {
                                                console.error('Erro ao exportar CSV:', error);
                                            }
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 rounded-b-lg"
                                    >
                                        <FileText className="w-4 h-4" />
                                        Exportar Excel (CSV)
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Filtros e Modo de Visualização */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                        <div className="flex flex-wrap gap-4 items-center">
                            <div className="flex items-center gap-2">
                                <Filter className="w-4 h-4 text-gray-500" />
                                <span className="text-sm font-medium text-gray-700">Filtros:</span>
                            </div>
                            <select
                                value={filterProfissional || ''}
                                onChange={(e) => setFilterProfissional(e.target.value || null)}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                                <option value="">Todos os Profissionais</option>
                                {profissionais.map(prof => (
                                    <option key={prof} value={prof}>{prof}</option>
                                ))}
                            </select>
                            <select
                                value={filterPaciente || ''}
                                onChange={(e) => setFilterPaciente(e.target.value || null)}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                                <option value="">Todos os Pacientes</option>
                                {pacientes.map(pac => (
                                    <option key={pac} value={pac}>{pac}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setViewMode('calendar')}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                    viewMode === 'calendar'
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                <Calendar className="w-4 h-4 inline mr-2" />
                                Calendário
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                    viewMode === 'list'
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                Lista
                            </button>
                        </div>
                    </div>
                </div>

                {/* Calendário */}
                {viewMode === 'calendar' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                        {/* Navegação do Mês */}
                        <div className="flex items-center justify-between mb-6">
                            <button
                                onClick={() => setCurrentMonth(currentMonth.clone().subtract(1, 'month'))}
                                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                            >
                                <ChevronLeft className="w-5 h-5 text-gray-600" />
                            </button>
                            <h2 className="text-xl font-bold text-gray-900">
                                {currentMonth.format('MMMM [de] YYYY')}
                            </h2>
                            <button
                                onClick={() => setCurrentMonth(currentMonth.clone().add(1, 'month'))}
                                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                            >
                                <ChevronRight className="w-5 h-5 text-gray-600" />
                            </button>
                        </div>

                        {/* Grid do Calendário */}
                        <div className="grid grid-cols-7 gap-2">
                            {/* Cabeçalho dos dias da semana */}
                            {diasSemana.map(dia => (
                                <div key={dia} className="text-center text-sm font-semibold text-gray-600 py-2">
                                    {dia}
                                </div>
                            ))}

                            {/* Dias do mês */}
                            {diasDoMes.map((dia, index) => {
                                const dataStr = dia.data.format('YYYY-MM-DD');
                                const itensDia = itensPorData[dataStr] || [];
                                const isToday = dia.data.isSame(moment(), 'day');
                                const temPlantoes = itensDia.length > 0;

                                return (
                                    <div
                                        key={index}
                                        onClick={() => {
                                            if (dia.isInPeriod) {
                                                setDiaSelecionado(dia.data);
                                                setShowDetalhesDia(true);
                                            }
                                        }}
                                        className={`min-h-[120px] border rounded-lg p-2 transition-all ${
                                            !dia.isCurrentMonth
                                                ? 'bg-gray-50 border-gray-200 opacity-50'
                                                : dia.isInPeriod
                                                ? 'bg-white border-gray-300'
                                                : 'bg-gray-50 border-gray-200'
                                        } ${isToday ? 'ring-2 ring-primary-500' : ''} ${
                                            dia.isInPeriod ? 'cursor-pointer hover:shadow-md hover:border-primary-400' : ''
                                        }`}
                                    >
                                        <div className={`text-sm font-medium mb-1 ${
                                            isToday ? 'text-primary-600' : dia.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                                        }`}>
                                            {dia.data.format('D')}
                                        </div>
                                        <div className="space-y-1 overflow-y-auto max-h-[90px]">
                                            {itensDia.length > 0 ? (
                                                itensDia.map((item, idx) => (
                                                    <div
                                                        key={idx}
                                                        className={`text-xs p-1 rounded border ${getTipoPlantaoColor(item.tipo_plantao)}`}
                                                        title={`${item.usuario_nome} - ${formatarHora(item.hora_inicio)} às ${formatarHora(item.hora_fim)}`}
                                                    >
                                                        <div className="font-medium truncate">{item.usuario_nome}</div>
                                                        <div className="text-xs opacity-75">
                                                            {formatarHora(item.hora_inicio)} - {formatarHora(item.hora_fim)}
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                dia.isInPeriod && (
                                                    <div className="text-xs text-gray-400 italic text-center py-1">
                                                        Sem plantões
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Lista */}
                {viewMode === 'list' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            Plantões ({itensFiltrados.length})
                        </h3>
                        <div className="space-y-4 max-h-[600px] overflow-y-auto">
                            {Object.entries(itensPorData)
                                .sort(([a], [b]) => moment(a).diff(moment(b)))
                                .map(([data, itensData]) => (
                                    <div key={data} className="border-b border-gray-200 pb-4 last:border-b-0">
                                        <h4 className="font-semibold text-gray-900 mb-3">
                                            {moment(data).format('dddd, DD [de] MMMM [de] YYYY')}
                                        </h4>
                                        <div className="space-y-2">
                                            {itensData
                                                .sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio))
                                                .map((item, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <User className="w-4 h-4 text-gray-500" />
                                                                    <span className="font-medium text-gray-900">
                                                                        {item.usuario_nome}
                                                                    </span>
                                                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTipoPlantaoColor(item.tipo_plantao)}`}>
                                                                        {item.tipo_plantao}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center gap-4 text-sm text-gray-600 ml-6">
                                                                    <span className="flex items-center gap-1">
                                                                        <Clock className="w-3 h-3" />
                                                                        {formatarHora(item.hora_inicio)} - {formatarHora(item.hora_fim)}
                                                                    </span>
                                                                    {item.paciente_nome && (
                                                                        <span className="flex items-center gap-1">
                                                                            <Users className="w-3 h-3" />
                                                                            {item.paciente_nome}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}

                {/* Estatísticas */}
                <EstatisticasDetalhadas
                    itens={escala.itens}
                    estatisticas={escala.estatisticas}
                    periodo={{
                        inicio: escala.periodo_inicio,
                        fim: escala.periodo_fim
                    }}
                />
            </div>

            {/* Modal de Detalhes do Dia */}
            <Modal
                isOpen={showDetalhesDia && !!diaSelecionado}
                onClose={() => {
                    setShowDetalhesDia(false);
                    setDiaSelecionado(null);
                }}
                title={diaSelecionado ? `Detalhes do Dia - ${diaSelecionado.format('dddd, DD [de] MMMM [de] YYYY')}` : 'Detalhes do Dia'}
                size="xl"
            >
                {diaSelecionado && (
                    <DetalhesDia
                        data={diaSelecionado.format('YYYY-MM-DD')}
                        itens={itensPorData[diaSelecionado.format('YYYY-MM-DD')] || []}
                        getTipoPlantaoColor={getTipoPlantaoColor}
                        formatarHora={formatarHora}
                    />
                )}
            </Modal>

            {/* Modal de Confirmação de Exclusão */}
            {showConfirmarExclusao && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <div className="flex items-start gap-3 mb-4">
                            <AlertCircle className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Excluir Escala
                                </h3>
                                <p className="text-sm text-gray-600 mb-3">
                                    Tem certeza que deseja excluir esta escala de plantão?
                                </p>
                                {escala && (
                                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                                        <p className="text-xs text-gray-500 mb-1">Período:</p>
                                        <p className="text-sm font-medium text-gray-900">
                                            {moment(escala.periodo_inicio).format('DD/MM/YYYY')} - {moment(escala.periodo_fim).format('DD/MM/YYYY')}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-2 mb-1">Status:</p>
                                        <p className="text-sm font-medium text-gray-900 capitalize">
                                            {escala.status}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-2 mb-1">Total de Plantões:</p>
                                        <p className="text-sm font-medium text-gray-900">
                                            {escala.itens?.length || 0}
                                        </p>
                                    </div>
                                )}
                                <p className="text-xs text-red-600 font-medium">
                                    ⚠️ Esta ação não pode ser desfeita. Todos os plantões desta escala serão excluídos permanentemente.
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <SecondaryButton
                                onClick={() => setShowConfirmarExclusao(false)}
                                disabled={excluindo}
                            >
                                Cancelar
                            </SecondaryButton>
                            <PrimaryButton
                                onClick={async () => {
                                    setExcluindo(true);
                                    try {
                                        await excluirEscala(id);
                                        setShowConfirmarExclusao(false);
                                        // Redirecionar para a lista de escalas após exclusão
                                        navigate('/escalas', { 
                                            state: { 
                                                message: 'Escala excluída com sucesso!',
                                                type: 'success'
                                            } 
                                        });
                                    } catch (error) {
                                        const errorMessage = error.response?.data?.message || 'Erro ao excluir escala';
                                        setError(errorMessage);
                                        setShowConfirmarExclusao(false);
                                    } finally {
                                        setExcluindo(false);
                                    }
                                }}
                                disabled={excluindo}
                                className="bg-red-600 hover:bg-red-700"
                            >
                                {excluindo ? (
                                    <span className="flex items-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Excluindo...
                                    </span>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Excluir Escala
                                    </>
                                )}
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Componente para exibir detalhes de um dia específico com timeline de 24 horas
 */
const DetalhesDia = ({ data, itens, getTipoPlantaoColor, formatarHora }) => {
    // Gerar array de 24 horas
    const horas = Array.from({ length: 24 }, (_, i) => i);
    
    // Criar timeline com plantões
    const timeline = useMemo(() => {
        const timelineData = horas.map(hora => ({
            hora,
            horaFormatada: `${hora.toString().padStart(2, '0')}:00`,
            plantoes: []
        }));

        itens.forEach(item => {
            const inicio = moment(`${data} ${item.hora_inicio}`, 'YYYY-MM-DD HH:mm:ss');
            const fim = moment(`${data} ${item.hora_fim}`, 'YYYY-MM-DD HH:mm:ss');
            
            // Ajustar para plantões noturnos que passam da meia-noite
            let fimAjustado = fim;
            if (item.tipo_plantao === 'noturno' || item.tipo_plantao === 'integral') {
                if (fim.isBefore(inicio) || fim.hour() < inicio.hour()) {
                    fimAjustado = fim.clone().add(1, 'day');
                }
            }

            const horaInicio = inicio.hour();
            const horaFim = fimAjustado.hour() === 0 ? 24 : fimAjustado.hour();

            // Adicionar plantão às horas que ele cobre
            for (let h = horaInicio; h < horaFim; h++) {
                if (h < 24) {
                    timelineData[h].plantoes.push({
                        ...item,
                        horaInicio: inicio.hour(),
                        minutoInicio: inicio.minute(),
                        horaFim: fimAjustado.hour() === 0 ? 24 : fimAjustado.hour(),
                        minutoFim: fimAjustado.minute()
                    });
                }
            }
        });

        return timelineData;
    }, [data, itens, horas]);

    if (!itens || itens.length === 0) {
        return (
            <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-yellow-600" />
                    <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                        Nenhum plantão agendado para este dia
                    </h3>
                    <p className="text-sm text-yellow-700">
                        Não há profissionais de plantão agendados para {moment(data).format('DD/MM/YYYY')}.
                    </p>
                </div>
                
                {/* Timeline vazia */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Timeline de 24 Horas
                        </h3>
                    </div>
                    <div className="max-h-[500px] overflow-y-auto">
                        {horas.map((hora, index) => {
                            const horaFormatada = `${hora.toString().padStart(2, '0')}:00`;
                            const isMadrugada = hora >= 0 && hora < 6;
                            const isManha = hora >= 6 && hora < 12;
                            const isTarde = hora >= 12 && hora < 18;
                            const isNoite = hora >= 18 && hora < 24;

                            return (
                                <div
                                    key={index}
                                    className={`border-b border-gray-100 p-3 ${
                                        isMadrugada
                                            ? 'bg-indigo-50'
                                            : isManha
                                            ? 'bg-yellow-50'
                                            : isTarde
                                            ? 'bg-orange-50'
                                            : isNoite
                                            ? 'bg-purple-50'
                                            : 'bg-white'
                                    }`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="w-16 flex-shrink-0">
                                            <div className="font-semibold text-gray-900 text-sm">
                                                {horaFormatada}
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <div className="text-sm text-gray-400 italic">
                                                Sem plantão neste horário
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Resumo do Dia */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4">
                    <div>
                        <div className="text-sm text-blue-600 mb-1">Total de Plantões</div>
                        <div className="text-2xl font-bold text-blue-900">{itens.length}</div>
                    </div>
                    <div>
                        <div className="text-sm text-blue-600 mb-1">Profissionais</div>
                        <div className="text-2xl font-bold text-blue-900">
                            {new Set(itens.map(i => i.usuario_nome)).size}
                        </div>
                    </div>
                    <div>
                        <div className="text-sm text-blue-600 mb-1">Horas Cobertas</div>
                        <div className="text-2xl font-bold text-blue-900">
                            {calcularHorasCobertas(itens, data)}h
                        </div>
                    </div>
                </div>
            </div>

            {/* Timeline de 24 Horas */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Timeline de 24 Horas
                    </h3>
                </div>
                <div className="max-h-[500px] overflow-y-auto">
                    {timeline.map((slot, index) => {
                        const temPlantoes = slot.plantoes.length > 0;
                        const isMadrugada = slot.hora >= 0 && slot.hora < 6;
                        const isManha = slot.hora >= 6 && slot.hora < 12;
                        const isTarde = slot.hora >= 12 && slot.hora < 18;
                        const isNoite = slot.hora >= 18 && slot.hora < 24;

                        return (
                            <div
                                key={index}
                                className={`border-b border-gray-100 p-3 transition-colors ${
                                    temPlantoes
                                        ? 'bg-green-50 hover:bg-green-100'
                                        : isMadrugada
                                        ? 'bg-indigo-50'
                                        : isManha
                                        ? 'bg-yellow-50'
                                        : isTarde
                                        ? 'bg-orange-50'
                                        : isNoite
                                        ? 'bg-purple-50'
                                        : 'bg-white'
                                }`}
                            >
                                <div className="flex items-start gap-4">
                                    {/* Hora */}
                                    <div className="w-16 flex-shrink-0">
                                        <div className="font-semibold text-gray-900 text-sm">
                                            {slot.horaFormatada}
                                        </div>
                                    </div>

                                    {/* Plantões */}
                                    <div className="flex-1 min-w-0">
                                        {temPlantoes ? (
                                            <div className="space-y-2">
                                                {slot.plantoes.map((plantao, idx) => {
                                                    // Verificar se este é o início do plantão
                                                    const isInicio = slot.hora === plantao.horaInicio;
                                                    // Verificar se este é o fim do plantão
                                                    const isFim = slot.hora === plantao.horaFim - 1 || 
                                                                 (plantao.horaFim === 24 && slot.hora === 23);

                                                    return (
                                                        <div
                                                            key={idx}
                                                            className={`p-2 rounded border ${getTipoPlantaoColor(plantao.tipo_plantao)}`}
                                                        >
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex-1">
                                                                    <div className="flex items-center gap-2 mb-1">
                                                                        <User className="w-4 h-4" />
                                                                        <span className="font-medium text-sm">
                                                                            {plantao.usuario_nome}
                                                                        </span>
                                                                        <span className="text-xs opacity-75">
                                                                            {plantao.tipo_plantao}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex items-center gap-4 text-xs ml-6">
                                                                        <span className="flex items-center gap-1">
                                                                            <Clock className="w-3 h-3" />
                                                                            {formatarHora(plantao.hora_inicio)} - {formatarHora(plantao.hora_fim)}
                                                                        </span>
                                                                        {plantao.paciente_nome && (
                                                                            <span className="flex items-center gap-1">
                                                                                <Users className="w-3 h-3" />
                                                                                {plantao.paciente_nome}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {(isInicio || isFim) && (
                                                                    <div className="text-xs text-gray-500 ml-2">
                                                                        {isInicio && 'Início'}
                                                                        {isFim && 'Fim'}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="text-sm text-gray-400 italic">
                                                Sem plantão neste horário
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Lista de Plantões do Dia */}
            <div className="border border-gray-200 rounded-lg">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Plantões do Dia
                    </h3>
                </div>
                <div className="p-4 space-y-3">
                    {itens
                        .sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio))
                        .map((item, index) => (
                            <div
                                key={index}
                                className={`p-3 rounded-lg border ${getTipoPlantaoColor(item.tipo_plantao)}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <User className="w-4 h-4" />
                                            <span className="font-medium">{item.usuario_nome}</span>
                                            <span className="text-xs opacity-75">{item.tipo_plantao}</span>
                                        </div>
                                        <div className="flex items-center gap-4 text-sm ml-6">
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                {formatarHora(item.hora_inicio)} - {formatarHora(item.hora_fim)}
                                            </span>
                                            {item.paciente_nome && (
                                                <span className="flex items-center gap-1">
                                                    <Users className="w-3 h-3" />
                                                    {item.paciente_nome}
                                                </span>
                                            )}
                                        </div>
                                        {item.justificativa && (
                                            <div className="text-xs text-gray-600 mt-2 ml-6 italic">
                                                {item.justificativa}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                </div>
            </div>
        </div>
    );
};

/**
 * Calcula o total de horas cobertas no dia
 */
const calcularHorasCobertas = (itens, data) => {
    const horasCobertas = new Set();
    
    itens.forEach(item => {
        const inicio = moment(`${data} ${item.hora_inicio}`, 'YYYY-MM-DD HH:mm:ss');
        let fim = moment(`${data} ${item.hora_fim}`, 'YYYY-MM-DD HH:mm:ss');
        
        if (item.tipo_plantao === 'noturno' || item.tipo_plantao === 'integral') {
            if (fim.isBefore(inicio) || fim.hour() < inicio.hour()) {
                fim = fim.clone().add(1, 'day');
            }
        }

        const horaInicio = inicio.hour();
        const horaFim = fim.hour() === 0 ? 24 : fim.hour();

        for (let h = horaInicio; h < horaFim; h++) {
            if (h < 24) {
                horasCobertas.add(h);
            }
        }
    });

    return horasCobertas.size;
};

export default DetalhesEscalaPage;

